import { H2 } from '@wheely/ui-kit';
import React, { useMemo } from 'react';
import { Control, type UseControllerProps } from 'react-hook-form';
import { useIntl, FormattedMessage } from 'react-intl';

import { useExternalLinks } from '@/app/hooks/useExternalLinks';
import { WelcomeForm } from '@/modules/onboarding/model/welcomeStep';
import { capitalizeString } from '@/shared/utils/capitalize';
import { emailValidator } from '@/shared/utils/email-validator';
import { incorrectValueMessage, NAME_PATTERN, checkNoDigits } from '@/shared/utils/name-validation';
import { BottomButtons } from '@/shared/view/BottomButtons';
import { Button } from '@src-common/view/adapters/Button';
import { SelectField } from '@src-common/view/adapters/reactHookForm/SelectField';
import { TextField } from '@src-common/view/adapters/reactHookForm/TextField';
import { ISubListItem } from '@src-common/view/components/ListInput';

import s from './style.module.css';

interface IProps {
  control: Control<WelcomeForm>;
  onSubmit: (e?: React.BaseSyntheticEvent) => void;
  cities: ISubListItem[];
  isSubmitting?: boolean;
}

type WelcomeFormRules = UseControllerProps<WelcomeForm>['rules'];

const WelcomeView: React.FC<IProps> = ({ isSubmitting, control, onSubmit, cities }) => {
  const intl = useIntl();
  const links = useExternalLinks();

  const noDigitsFieldRules = useMemo<WelcomeFormRules>(
    () => ({
      validate: (value: string | null) =>
        checkNoDigits(value, intl.formatMessage(incorrectValueMessage)),
    }),
    [intl],
  );

  const firstNameFieldRules = useMemo<WelcomeFormRules>(
    () => ({
      pattern: {
        value: NAME_PATTERN,
        message: intl.formatMessage(incorrectValueMessage),
      },
    }),
    [intl],
  );

  const emailRules = useMemo<WelcomeFormRules>(
    () => ({
      validate: (value: string | null) =>
        emailValidator(
          value,
          intl.formatMessage({
            defaultMessage: 'Incorrect value',
            id: 'f9Ik/S',
            description: 'Field error message',
          }),
        ),
    }),
    [intl],
  );

  const sourceTypes = useMemo(
    () => [
      {
        title: intl.formatMessage({
          defaultMessage: 'Billboard advertising',
          id: '3z1mnu',
          description: 'select option title',
        }),
        value: 'billboard_advertising',
      },
      {
        title: intl.formatMessage({
          defaultMessage: 'I am a Returning Chauffeur',
          id: '02Y14g',
          description: 'select option title',
        }),
        value: 'i_am_returning_chauffeur',
      },
      {
        title: intl.formatMessage({
          defaultMessage: 'Job Board e.g. Indeed',
          id: 'dOQgBX',
          description: 'select option title',
        }),
        value: 'job_board',
      },
      {
        title: intl.formatMessage({
          defaultMessage: 'LinkedIn',
          id: 'x2+8ac',
          description: 'select option title',
        }),
        value: 'linked_in',
      },
      {
        title: intl.formatMessage({
          defaultMessage: 'Referral from an Existing Chauffeur',
          id: 'vigbdf',
          description: 'select option title',
        }),
        value: 'referral',
      },
      {
        title: intl.formatMessage({
          defaultMessage: 'Referral from a Partner Company',
          id: 'o6F6QA',
          description: 'select option title',
        }),
        value: 'chauffeur_company',
      },
      {
        title: intl.formatMessage({
          defaultMessage: 'Search Engines / Wheely Website',
          id: 'kc6LZ5',
          description: 'select option title',
        }),
        value: 'search_engines_wheely_website',
      },
      {
        title: intl.formatMessage({
          defaultMessage: 'Social Media e.g. Facebook, Instagram',
          id: 'D8Q0ot',
          description: 'select option title',
        }),
        value: 'social_media',
      },
      {
        title: intl.formatMessage({
          defaultMessage: 'Outreach from Wheely team',
          id: 'FINBFm',
          description: 'select option title',
        }),
        value: 'outreach_from_wheely_team',
      },
      {
        title: intl.formatMessage({
          defaultMessage: 'Other',
          id: 'b9He/z',
          description: 'select option title',
        }),
        value: 'other',
      },
    ],
    [intl],
  );

  return (
    <form className={s.container} onSubmit={onSubmit}>
      <H2>
        <FormattedMessage
          data-test-id="wizard__welcome-title"
          defaultMessage="WELCOME"
          id="sE8VyT"
          description="Onboarding welcome step title"
        />
      </H2>
      <p className={s.annotation} data-test-id="wizard__welcome-sub-title">
        <FormattedMessage
          defaultMessage="Simply complete the form for chauffeurs to get started with your Wheely Accreditation. This usually takes 15–20 minutes."
          id="K+t3oQ"
          description="Onboarding welcome step description"
        />
      </p>
      <div className={s.fieldRow}>
        <SelectField
          control={control}
          name="cityId"
          placeholder={intl.formatMessage({
            defaultMessage: 'City',
            id: 'GzIuab',
            description: 'City field placeholder',
          })}
          items={cities}
        />
      </div>
      <div className={s.fieldRow}>
        <TextField
          name="firstName"
          placeholder={intl.formatMessage({
            defaultMessage: 'First name',
            id: 'Joox67',
            description: 'First  name field placeholder',
          })}
          transformValue={capitalizeString}
          control={control}
          isRequired={true}
          rules={firstNameFieldRules}
        />
      </div>
      <div className={s.fieldRow}>
        <TextField
          name="middleName"
          transformValue={capitalizeString}
          placeholder={intl.formatMessage({
            defaultMessage: 'Middle name (optional)',
            id: 'RooHxs',
            description: 'Middle name field placeholder',
          })}
          control={control}
          rules={noDigitsFieldRules}
        />
      </div>
      <div className={s.fieldRow}>
        <TextField
          name="lastName"
          transformValue={capitalizeString}
          placeholder={intl.formatMessage({
            defaultMessage: 'Last name',
            id: 'PirYGi',
            description: 'Last name field placeholder',
          })}
          control={control}
          isRequired={true}
          rules={noDigitsFieldRules}
        />
      </div>
      <div className={s.fieldRow}>
        <TextField
          rules={emailRules}
          name="email"
          placeholder={intl.formatMessage({
            defaultMessage: 'Email',
            id: 'Risxrn',
            description: 'Email field placeholder',
          })}
          control={control}
          isRequired={true}
        />
      </div>
      <div className={s.fieldRow}>
        <SelectField
          name="source"
          control={control}
          placeholder={intl.formatMessage({
            defaultMessage: 'How did you hear about Wheely?',
            id: 'g57W42',
            description: 'field placeholder',
          })}
          items={sourceTypes}
        />
      </div>
      <div className={s.fieldRow}>
        <TextField
          name="referralCode"
          placeholder={intl.formatMessage({
            defaultMessage: 'Referral code (optional)',
            id: '9czCvP',
            description: 'Referral code field placeholder',
          })}
          control={control}
        />
      </div>

      <BottomButtons>
        <Button type="submit" isPending={isSubmitting}>
          <FormattedMessage
            defaultMessage="NEXT"
            id="6jg2Fp"
            description="OnBoarding next button text"
          />
        </Button>
      </BottomButtons>

      <p className={s.footer} data-test-id="sign-up-us-partner">
        <FormattedMessage
          defaultMessage="If you have got several vehicles and chauffeurs, <link>sign up as a partner.</link>"
          id="mlN8iv"
          description="OnBoarding welcome footer text - sign up as a partner"
          values={{
            link: (chunks: React.ReactNode) => <a href={links.chauffeurLanding}>{chunks}</a>,
          }}
        />
      </p>
    </form>
  );
};

export { WelcomeView };
